import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { HeartIcon } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SeoHead, { breadcrumbLd, itemListLd } from "@/components/SeoHead";

export default function Browse() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(
    () => ({
      category: params.get("category") || "",
      type: params.get("type") || "",
      segment: params.get("segment") || "",
      q: params.get("q") || "",
      following: params.get("following") === "1",
    }),
    [params]
  );

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories));
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = { status: "active" };
    if (filters.category) query.category = filters.category;
    if (filters.type) query.type = filters.type;
    if (filters.segment) query.segment = filters.segment;
    if (filters.q) query.q = filters.q;
    if (filters.following) query.following = true;
    api
      .get("/listings", { params: query })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "_all") next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="browse-page">
      <SeoHead
        title={
          filters.q
            ? `${filters.q} — Bidorah marketplace`
            : filters.category && filters.category !== "_all"
              ? `${filters.category} auctions & drops`
              : "Browse live auctions and verified listings"
        }
        description={
          filters.category && filters.category !== "_all"
            ? `Shop ${filters.category} live auctions and Buy-It-Now drops on Bidorah. Every seller US-verified. Bid live, win real.`
            : "Browse Bidorah's live auctions and Buy-It-Now drops — sneakers, trading cards, jewelry, fragrances and more from US-verified sellers."
        }
        canonical={
          filters.category && filters.category !== "_all"
            ? `https://bidorah.com/category/${encodeURIComponent(filters.category)}`
            : "https://bidorah.com/browse"
        }
        jsonLd={[
          breadcrumbLd([
            { name: "Home", url: "https://bidorah.com/" },
            { name: "Browse", url: "https://bidorah.com/browse" },
            ...(filters.category && filters.category !== "_all"
              ? [{ name: filters.category, url: `https://bidorah.com/category/${encodeURIComponent(filters.category)}` }]
              : []),
          ]),
          itemListLd(items, filters.category || "Bidorah marketplace"),
        ]}
      />
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          {filters.q ? `Results for "${filters.q}"` : "Browse the marketplace"}
        </h1>

        <div className="mt-8 border border-zinc-200 bg-zinc-50 p-4 flex flex-wrap gap-3 items-center">
          <span className="text-sm font-semibold text-zinc-500 mr-2">Filters</span>
          <Select
            value={filters.category || "_all"}
            onValueChange={(v) => setFilter("category", v)}
          >
            <SelectTrigger className="w-[220px] bg-white" data-testid="filter-category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.type || "_all"}
            onValueChange={(v) => setFilter("type", v)}
          >
            <SelectTrigger className="w-[180px] bg-white" data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All types</SelectItem>
              <SelectItem value="fixed">Buy It Now</SelectItem>
              <SelectItem value="auction">Auction</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.segment || "_all"}
            onValueChange={(v) => setFilter("segment", v)}
          >
            <SelectTrigger className="w-[180px] bg-white" data-testid="filter-segment">
              <SelectValue placeholder="Segment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All segments</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="wholesale">Wholesale</SelectItem>
            </SelectContent>
          </Select>
          {user && (
            <button
              type="button"
              onClick={() => setFilter("following", filters.following ? null : "1")}
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-bold tracking-wide border transition-colors ${
                filters.following
                  ? "bg-[#1D78AB] text-white border-[#1D78AB] shadow-sm"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB]"
              }`}
              data-testid="filter-following"
              aria-pressed={filters.following}
            >
              <HeartIcon size={13} weight={filters.following ? "fill" : "regular"} />
              Following
            </button>
          )}
          {(filters.category || filters.type || filters.segment || filters.q || filters.following) && (
            <Button
              variant="ghost"
              onClick={() => setParams(new URLSearchParams())}
              data-testid="clear-filters-btn"
            >
              Clear
            </Button>
          )}
        </div>

        <div className="mt-6">
          {loading ? (
            <p className="text-sm text-zinc-500 mt-8">Loading listings…</p>
          ) : items.length === 0 ? (
            <div className="border border-zinc-200 p-16 text-center mt-8 bg-white">
              <p className="font-display font-bold text-2xl">
                {filters.following
                  ? "No active listings from sellers you follow."
                  : "Nothing matches these filters yet."}
              </p>
              <p className="text-sm text-zinc-600 mt-2">
                {filters.following
                  ? "Tap Clear to see every listing on Bidorah."
                  : "Try removing a filter, or check back soon."}
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.map((l) => (
                <ProductCard key={l.id} listing={l} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
